// Form rules for a tier, apart from the component that draws them.
// They mirror data/schemas/tier.js and data/models/work/tier.js.

export const NAME_MAX = 160;
export const EXPECTED_OUTCOME_MAX = 4000;
export const QUESTION_MAX = 500;
export const QUESTIONS_MAX = 50;

// The domain state. Only two: a tier is being worked or it is over.
export const TIER_STATE_OPTIONS = [
    { value: 'active', label: 'Activo', hint: 'Se está trabajando' },
    { value: 'closed', label: 'Cerrado', hint: 'Pasó su compuerta' },
];

export const STATUS_OPTIONS = [
    { value: 2, label: 'Activo' },
    { value: 1, label: 'Inactivo' },
];

// Read only. The published contract does not accept `phase` on create or on
// update — it is the project's life stage and the system moves it. The list
// DOES filter by it, which is why the labels are here.
//
// The phase lives on the tier and not on the project: projects.bootstrap_phase
// only covers the two phases that happen before any tier exists.
export const TIER_PHASE_LABEL = {
    discovery:     'Descubrimiento',
    architecture:  'Arquitectura',
    construction:  'Construcción',
    stabilization: 'Estabilización',
    delivery:      'Entrega',
    operation:     'Operación',
};

// READ ONLY, and a THIRD axis — not a synonym of either of the other two.
//
//   breakdown_state  is the scope agreed?          draft · agreed · legacy
//   phase            where is the project's life?  discovery … operation
//   tier_state       is it being worked?           active · closed
//
// Collapsing them into one dropdown called "Estado" is the fastest way to make
// this screen lie. `legacy` is not a state anything moves to: it is what the
// 0021 migration marked on every tier that existed before the breakdown, and
// nothing reaches it afterwards.
export const BREAKDOWN_STATE_LABEL = {
    draft:  'Borrador',
    agreed: 'Acordado',
    legacy: 'Legado',
};

export const EMPTY_VALUES = {
    project_id: '',
    name: '',
    description: '',
    expected_outcome: '',
    open_questions: '',
    tier_state: 'active',
    sort_order: 0,
    status: 2,
};

// `open_questions` is a JSONB list of non-empty strings in the contract and one
// item per line in the form. A line is what a person types; a JSON array is
// what the API takes, and translating between the two is this module's job.
function linesToArray(text) {
    return text.split('\n').map(line => line.trim()).filter(Boolean);
}

function arrayToLines(value) {
    return Array.isArray(value) ? value.join('\n') : '';
}

export function valuesFrom(tier) {
    return {
        project_id: tier?.project_id != null ? String(tier.project_id) : '',
        name: tier?.name ?? '',
        description: tier?.description ?? '',
        expected_outcome: tier?.expected_outcome ?? '',
        open_questions: arrayToLines(tier?.open_questions),
        tier_state: tier?.tier_state ?? 'active',
        sort_order: tier?.sort_order ?? 0,
        status: tier?.status ?? 2,
    };
}

export function validate(values, { withProject }) {
    const errors = {};
    const name = values.name.trim();

    if (withProject && !values.project_id) errors.project_id = 'El proyecto es requerido';

    if (!name) errors.name = 'El nombre del tier es requerido';
    else if (name.length > NAME_MAX) errors.name = `No puede pasar de ${NAME_MAX} caracteres`;

    if (values.expected_outcome.length > EXPECTED_OUTCOME_MAX) {
        errors.expected_outcome = `No puede pasar de ${EXPECTED_OUTCOME_MAX} caracteres`;
    }

    const questions = linesToArray(values.open_questions);
    if (questions.length > QUESTIONS_MAX) {
        errors.open_questions = `No más de ${QUESTIONS_MAX} dudas: si hay tantas, el tier no está listo para prepararse`;
    } else if (questions.some(question => question.length > QUESTION_MAX)) {
        errors.open_questions = `Una duda no puede pasar de ${QUESTION_MAX} caracteres`;
    }

    if (Number(values.sort_order) < 0) errors.sort_order = 'El orden no puede ser negativo';

    return errors;
}

// THE FOUR FIELDS THAT COST A REVISION.
//
// Changing any of them is changing what the tier says it will build, so
// `PATCH /admin/tiers/{id}` demands the `breakdown_revision` that was read:
// without it 400, stale 409 (gate: revision), and a tier holding an authorized
// task 409 (gate: congelado). `sort_order`, `tier_state` and `status` are not
// content and ask for nothing.
const CONTENT_FIELDS = ['name', 'description', 'expected_outcome', 'open_questions'];

// PATCH does NOT take project_id: a tier cannot be moved to another project
// from here, and sending it would be refused. Create does take it.
//
// `plan_version_id` IS REQUIRED ON CREATE since T01 and this payload does not
// carry it — a tier now comes out of an approved plan version, and picking one
// needs the plans surface that T07.04 builds. Until then the create form says
// so instead of sending a body the API answers 422 to. Recorded as HT07.01-a.
export function toCreatePayload(values) {
    const payload = {
        project_id: Number(values.project_id),
        name: values.name.trim(),
        description: values.description.trim() || null,
        tier_state: values.tier_state,
        sort_order: Number(values.sort_order) || 0,
        status: Number(values.status),
    };

    const outcome = values.expected_outcome.trim();
    if (outcome) payload.expected_outcome = outcome;

    const questions = linesToArray(values.open_questions);
    if (questions.length) payload.open_questions = questions;

    return payload;
}

// ONLY WHAT CHANGED, and the revision only when content changed.
//
// Resending every field looks harmless and is not: a tier whose tasks are
// authorized is frozen, so a PATCH that repeats its name verbatim still asks
// to write a frozen column and comes back 409. The trigger raises the revision
// on an EFFECTIVE change, so the comparison here is against the row that was
// read, not against the empty form.
//
// `revision` is the one that came with that row. It is deliberately NOT
// refetched right before saving: re-reading to "avoid the 409" is how the
// second writer silently overwrites the first, which is the single thing this
// number exists to prevent.
export function toUpdatePayload(values, original, revision) {
    const from = valuesFrom(original);
    const payload = {};

    if (values.name.trim() !== from.name.trim()) {
        payload.name = values.name.trim();
    }
    if (values.description.trim() !== from.description.trim()) {
        payload.description = values.description.trim() || null;
    }
    if (values.expected_outcome.trim() !== from.expected_outcome.trim()) {
        payload.expected_outcome = values.expected_outcome.trim() || null;
    }
    if (values.open_questions.trim() !== from.open_questions.trim()) {
        payload.open_questions = linesToArray(values.open_questions);
    }

    if (values.tier_state !== from.tier_state) payload.tier_state = values.tier_state;
    if (Number(values.sort_order) !== Number(from.sort_order)) {
        payload.sort_order = Number(values.sort_order) || 0;
    }
    if (Number(values.status) !== Number(from.status)) payload.status = Number(values.status);

    const touchedContent = CONTENT_FIELDS.some(field => field in payload);
    if (touchedContent && revision != null) payload.breakdown_revision = revision;

    return payload;
}

export function hasChanges(payload) {
    return Object.keys(payload).some(key => key !== 'breakdown_revision');
}
